// React wrapper for lib/windowDrag.js: press-and-hold on a control, move the
// pointer, and the whole desktop window follows.  Spread the returned props on
// the element that contains the controls.  See lib/windowDrag.js for why the
// page reports start / move / end instead of moving the window itself.
//
// `api` is { dragStart, dragMove, dragEnd } — window.electronAPI.timerBar on
// desktop 1.0.14+.  Without it (desktop 1.0.13, a browser) this returns no
// props and every press is an ordinary click, exactly as before.
//
// `canStart(event)` picks which presses may drag, by returning the ELEMENT the
// press belongs to (or null).  The timer bar only drags from its buttons:
// everywhere else is already a native drag region, and on some platforms a page
// still sees the press there — moving the window from both places at once
// would fight.
//
// The pointer is captured on that element AT PRESS TIME, not once the drag is
// recognised: a quick flick leaves a 48px-tall bar on its very first mouse
// event, and without capture those events never reach the page (proved under
// Electron).  It is captured on the pressed button, not on the container, so
// the button still receives its click — with capture on the container, the
// click would go to the container and the button would never fire.

import { useEffect, useMemo, useRef } from 'react'
import { createWindowDrag } from '../lib/windowDrag'

export function hasWindowDragApi (api) {
  return !!(api && typeof api.dragStart === 'function' && typeof api.dragMove === 'function' &&
    typeof api.dragEnd === 'function')
}

export default function useWindowDrag ({ api, canStart } = {}) {
  const frame = useRef(0)
  const apiRef = useRef(api)
  apiRef.current = api
  const canStartRef = useRef(canStart)
  canStartRef.current = canStart
  const captured = useRef(null)    // { el, pointerId } while a press holds capture

  const controller = useMemo(() => {
    const flushFrame = () => { if (frame.current) cancelAnimationFrame(frame.current); frame.current = 0 }
    return createWindowDrag({
      now: () => performance.now(),
      start: (dx, dy) => apiRef.current.dragStart(dx, dy),
      // One message per animation frame however fast the mouse reports —
      // the desktop app reads the mouse position itself when it moves.
      move: () => {
        if (frame.current) return
        frame.current = requestAnimationFrame(() => {
          frame.current = 0
          try { apiRef.current.dragMove() } catch {}
        })
      },
      // The desktop app places the bar at the mouse on end, so a move still
      // waiting for its frame is not needed.
      end: () => { flushFrame(); apiRef.current.dragEnd() }
    })
  }, [])

  useEffect(() => () => {
    if (frame.current) cancelAnimationFrame(frame.current)
    // Unmounted mid-drag (the bar re-rendered to a different state): never
    // leave the desktop app following the mouse.
    if (controller.isDragging()) { try { apiRef.current.dragEnd() } catch {} }
  }, [controller])

  if (!hasWindowDragApi(api)) return {}
  return {
    onPointerDown: (e) => {
      const el = typeof canStartRef.current === 'function' ? canStartRef.current(e) : e.currentTarget
      if (!el || !controller.down(e)) return
      try { el.setPointerCapture(e.pointerId); captured.current = { el, pointerId: e.pointerId } } catch { captured.current = null }
    },
    onPointerMove: (e) => {
      const state = controller.move(e)
      if (state === 'started' || state === 'dragging') e.preventDefault()
    },
    onPointerUp: (e) => {
      controller.up(e)
      // Released automatically after pointerup too; explicit for clarity.
      const c = captured.current
      captured.current = null
      if (c) { try { c.el.releasePointerCapture(c.pointerId) } catch {} }
    },
    onPointerCancel: (e) => { controller.cancel(e); captured.current = null },
    onLostPointerCapture: (e) => { controller.cancel(e); captured.current = null },
    // The bar's own right-click menu (TimerBar's onContextMenu) is about to
    // open, possibly while the left button is held — on a Mac, ctrl+click is
    // a left press.  The native menu takes the release, so drop the press now
    // rather than leave it waiting.  Capture phase and a different prop name,
    // so it never replaces the page's own onContextMenu.
    onContextMenuCapture: () => {
      controller.cancelPress()
      const c = captured.current
      captured.current = null
      if (c) { try { c.el.releasePointerCapture(c.pointerId) } catch {} }
    },
    // Capture phase, so the click that ends a drag never reaches the button.
    onClickCapture: (e) => {
      if (controller.consumeClick(e)) { e.preventDefault(); e.stopPropagation() }
    }
  }
}
